import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import GameLayout from '../layout/GameLayout';
import FloatingCard from '../ui/FloatingCard';
import VenueSearch from '../ui/VenueSearch';
import dropInService from '../../services/api/dropInService';
import PriceField, { centsToDollars, dollarsToCents, priceError } from '../payments/PriceField';
import { RefundPolicyField } from '../payments/RefundPolicy';
import { getUserSports, getAllSports } from '../../services/api/sportsService';
import '../../styles/features/dropIn.css';
import { eventTimeZone, toEventInputValue, viewerTimeZone } from '../../utils/eventTime';

const SKILL_LEVELS = [
  { value: 'recreational', label: '🟢 Recreational', desc: 'Casual, all-skill-levels welcome' },
  { value: 'intermediate', label: '🟡 Intermediate', desc: 'Some experience required' },
  { value: 'competitive',  label: '🔴 Competitive',  desc: 'High-skill, serious play' },
];

// Convert ISO string to datetime-local value (YYYY-MM-DDTHH:mm)
// The time at the venue, wherever the host is editing from (utils/eventTime)
const toDatetimeLocal = (iso, timeZone) => toEventInputValue(iso, timeZone);

const DropInEdit = ({ token, user, onLogout }) => {
  const { id }    = useParams();
  const navigate  = useNavigate();

  const [sports, setSports]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState(null);
  const [formData, setFormData]   = useState(null);
  const [priceLocked, setPriceLocked] = useState(false);

  // Load sports list and existing drop-in in parallel
  useEffect(() => {
    const loadSports = async () => {
      try {
        const userRes = await getUserSports();
        const data = userRes?.data?.sports || userRes?.sports || userRes?.data || [];
        if (Array.isArray(data) && data.length) return data;
      } catch { /* fall through */ }
      return getAllSports().then((res) => res.data || []).catch(() => []);
    };
    Promise.all([
      loadSports(),
      dropInService.getById(id),
    ]).then(([sportsData, dropInRes]) => {
      setSports(sportsData);
      const d = dropInRes.data?.dropIn || dropInRes.dropIn || dropInRes;
      setFormData({
        sport_id:      d.sportId      || d.sport_id      || '',
        title:         d.title        || '',
        description:   d.description  || '',
        skill_level:   d.skillLevel   || d.skill_level   || 'recreational',
        max_players:   d.maxPlayers   || d.max_players   || 12,
        scheduled_at:  toDatetimeLocal(d.scheduledAt || d.scheduled_at, eventTimeZone(d)),
        venue:         d.venue        || '',
        venue_address: d.venueAddress || d.venue_address || '',
        venue_lat:     d.venueLat     ?? d.venue_lat     ?? null,
        venue_lng:     d.venueLng     ?? d.venue_lng     ?? null,
        venue_place_id:d.venuePlaceId || d.venue_place_id|| '',
        court:         d.court        || '',
        notes:         d.notes        || '',
        visibility:    d.visibility   || 'public',
        max_waitlist:    d.maxWaitlist    ?? d.max_waitlist    ?? 5,
        players_per_team: d.playersPerTeam ?? d.players_per_team ?? 6,
        price:         centsToDollars(d.priceCents ?? d.price_cents ?? 0),
        refund_policy: d.refundPolicy ?? d.refund_policy ?? '',
      });
      // Someone has paid or is paying: the price can't change under them
      setPriceLocked((d.players || []).some((p) =>
        p.status !== 'withdrawn' && (p.payment_status === 'pending' || p.payment_status === 'paid')));
    }).catch((err) => {
      setError(err.message || 'Failed to load drop-in');
    }).finally(() => {
      setLoading(false);
    });
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleVenueChange = (venue) => {
    setFormData((prev) => ({
      ...prev,
      venue:          venue?.venue          || '',
      venue_address:  venue?.venue_address  || '',
      venue_lat:      venue?.venue_lat      ?? null,
      venue_lng:      venue?.venue_lng      ?? null,
      venue_place_id: venue?.venue_place_id || '',
      court:          venue?.court          ?? prev.court,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) return setError('Please provide a title.');
    if (!formData.scheduled_at) return setError('Please set a date and time.');
    if (priceError(formData.price)) return setError(priceError(formData.price));

    setSaving(true);
    setError(null);
    try {
      const payload = {
        title:        formData.title,
        description:  formData.description || null,
        skill_level:  formData.skill_level,
        max_players:  parseInt(formData.max_players),
        max_waitlist:     parseInt(formData.max_waitlist),
        players_per_team: parseInt(formData.players_per_team),
        // The wall time at the venue: the server reads it in the session's zone
        scheduled_at: formData.scheduled_at,
        timezone:     viewerTimeZone(),
        venue:        formData.venue        || null,
        venue_address:formData.venue_address|| null,
        venue_lat:    formData.venue_lat    ?? null,
        venue_lng:    formData.venue_lng    ?? null,
        venue_place_id:formData.venue_place_id || null,
        court:        formData.court        || null,
        notes:        formData.notes        || null,
        visibility:   formData.visibility   || 'public',
        refund_policy: formData.refund_policy || null,
      };
      // The server ignores an unchanged price; a locked one isn't sent at all
      if (!priceLocked) payload.price_cents = dollarsToCents(formData.price);
      await dropInService.update(id, payload);
      navigate(`/drop-in/${id}`);
    } catch (err) {
      setError(err.message || 'Failed to update drop-in');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <GameLayout token={token} onLogout={onLogout}>
        <div className="dropin-loading">⏳ Loading…</div>
      </GameLayout>
    );
  }

  if (error && !formData) {
    return (
      <GameLayout token={token} onLogout={onLogout}>
        <div className="dropin-empty">
          <div className="dropin-empty__icon">⚠️</div>
          <div className="dropin-empty__title">{error}</div>
        </div>
      </GameLayout>
    );
  }

  return (
    <GameLayout token={token} onLogout={onLogout}>
      <div className="dropin-form-page">
        <div className="dropin-form-header">
          <h1 className="dropin-form-title">✏️ Edit Drop-in Session</h1>
          <p className="dropin-form-subtitle">Update the details for this open-play event.</p>
        </div>

        {error && (
          <div className="form-error-banner" role="alert" style={{ marginBottom: '1rem' }}>
            ⚠️ {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Row 1: Event Details + Level & Capacity side by side */}
          <div className="dropin-form-row">
            <FloatingCard>
              <h3 className="form-section-title">📋 Event Details</h3>

              {/* Sport — read-only on edit to avoid data confusion */}
              <div className="form-group-gaming">
                <label className="form-label-gaming">Sport</label>
                <select
                  name="sport_id"
                  value={formData.sport_id}
                  disabled
                  className="form-select-gaming"
                  style={{ opacity: 0.6 }}
                >
                  <option value="">Select a sport</option>
                  {sports.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
                <div className="form-hint" style={{ marginTop: '0.3rem' }}>
                  <span className="hint-icon">ℹ️</span> Sport cannot be changed after creation
                </div>
              </div>

              {/* Title */}
              <div className="form-group-gaming">
                <label className="form-label-gaming">Title *</label>
                <input
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  maxLength={255}
                  className="form-input-gaming"
                />
              </div>

              {/* Description */}
              <div className="form-group-gaming">
                <label className="form-label-gaming">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  maxLength={1000}
                  className="form-input-gaming"
                  style={{ resize: 'vertical', fontFamily: 'inherit' }}
                />
              </div>
            </FloatingCard>

            <FloatingCard>
              <h3 className="form-section-title">🏅 Level & Capacity</h3>

              {/* Skill level */}
              <div className="form-group-gaming">
                <label className="form-label-gaming">Skill Level *</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {SKILL_LEVELS.map((sl) => (
                    <label
                      key={sl.value}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '0.6rem 0.85rem',
                        borderRadius: '10px',
                        background: formData.skill_level === sl.value
                          ? 'rgba(167,139,250,0.15)' : 'rgba(255,255,255,0.04)',
                        border: formData.skill_level === sl.value
                          ? '1px solid rgba(167,139,250,0.5)' : '1px solid rgba(255,255,255,0.08)',
                        cursor: 'pointer',
                        transition: 'background 0.18s, border-color 0.18s',
                      }}
                    >
                      <input
                        type="radio"
                        name="skill_level"
                        value={sl.value}
                        checked={formData.skill_level === sl.value}
                        onChange={handleChange}
                        style={{ accentColor: '#a78bfa' }}
                      />
                      <div>
                        <div style={{ fontWeight: 600, color: '#e2e8f0', fontSize: '0.875rem' }}>
                          {sl.label}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{sl.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Visibility */}
              <div className="form-group-gaming">
                <label className="form-label-gaming">Visibility</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {[
                    { value: 'public',  emoji: '🌐', label: 'Public',  desc: 'Anyone can find & join' },
                    { value: 'private', emoji: '🔒', label: 'Private', desc: 'Invite-only group session' },
                  ].map((opt) => (
                    <label
                      key={opt.value}
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.6rem 0.85rem',
                        borderRadius: '10px',
                        background: formData.visibility === opt.value
                          ? 'rgba(167,139,250,0.15)' : 'rgba(255,255,255,0.04)',
                        border: formData.visibility === opt.value
                          ? '1px solid rgba(167,139,250,0.5)' : '1px solid rgba(255,255,255,0.08)',
                        cursor: 'pointer',
                        transition: 'background 0.18s, border-color 0.18s',
                      }}
                    >
                      <input
                        type="radio"
                        name="visibility"
                        value={opt.value}
                        checked={formData.visibility === opt.value}
                        onChange={handleChange}
                        style={{ accentColor: '#a78bfa' }}
                      />
                      <div>
                        <div style={{ fontWeight: 600, color: '#e2e8f0', fontSize: '0.875rem' }}>
                          {opt.emoji} {opt.label}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{opt.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Price per player: shown only while payments are on */}
              <PriceField
                value={formData.price}
                onChange={(price) => setFormData((prev) => ({ ...prev, price }))}
                locked={priceLocked}
                lockedText="Players have already paid or are paying, so the price can't change."
              />
              <RefundPolicyField
                value={formData.refund_policy}
                price={formData.price}
                onChange={(refund_policy) => setFormData((prev) => ({ ...prev, refund_policy }))}
              />

              {/* Max players */}
              <div className="form-group-gaming">
                <label className="form-label-gaming">                  Waitlist Limit
                  <span style={{ color: '#94a3b8', fontWeight: 400 }}>
                    {' '}— {formData.max_waitlist === '0' || formData.max_waitlist === 0 ? 'disabled' : `${formData.max_waitlist} max`}
                  </span>
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <input
                    type="range"
                    name="max_waitlist"
                    min={0}
                    max={20}
                    value={formData.max_waitlist}
                    onChange={handleChange}
                    style={{ flex: 1, accentColor: '#a78bfa' }}
                  />
                  <input
                    type="number"
                    name="max_waitlist"
                    min={0}
                    max={50}
                    value={formData.max_waitlist}
                    onChange={handleChange}
                    className="form-input-gaming"
                    style={{ width: '70px', textAlign: 'center' }}
                  />
                </div>
                <div className="form-hint">
                  <span className="hint-icon">💡</span>
                  Set to 0 to disable the waitlist
                </div>
              </div>

              {/* Max players */}
              <div className="form-group-gaming">
                <label className="form-label-gaming">                  Max Players *
                  <span style={{ color: '#94a3b8', fontWeight: 400 }}>
                    {' '} — current: {formData.max_players}
                  </span>
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <input
                    type="range"
                    name="max_players"
                    min={2}
                    max={50}
                    value={formData.max_players}
                    onChange={handleChange}
                    style={{ flex: 1, accentColor: '#a78bfa' }}
                  />
                  <input
                    type="number"
                    name="max_players"
                    min={2}
                    max={100}
                    value={formData.max_players}
                    onChange={handleChange}
                    className="form-input-gaming"
                    style={{ width: '70px', textAlign: 'center' }}
                  />
                </div>
              </div>

              {/* Players per team */}
              <div className="form-group-gaming">
                <label className="form-label-gaming">                  Players per Team
                  <span style={{ color: '#94a3b8', fontWeight: 400 }}>
                    {' '}— {formData.players_per_team}
                  </span>
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <input
                    type="range"
                    name="players_per_team"
                    min={1}
                    max={20}
                    value={formData.players_per_team}
                    onChange={handleChange}
                    style={{ flex: 1, accentColor: '#a78bfa' }}
                  />
                  <input
                    type="number"
                    name="players_per_team"
                    min={1}
                    max={50}
                    value={formData.players_per_team}
                    onChange={handleChange}
                    className="form-input-gaming"
                    style={{ width: '70px', textAlign: 'center' }}
                  />
                </div>
                <div className="form-hint">
                  <span className="hint-icon">💡</span>
                  Volleyball: 6 · Soccer: 11 · Basketball: 5 · Badminton: 2
                </div>
              </div>
            </FloatingCard>
          </div>

          {/* Row 2: Schedule & Venue — full width */}
          <FloatingCard style={{ marginTop: '1rem' }}>
            <h3 className="form-section-title">📅 Schedule & Venue</h3>

            {/* Date/Time */}
            <div className="form-group-gaming">
              <label className="form-label-gaming">Date & Time *</label>
              <input
                type="datetime-local"
                name="scheduled_at"
                value={formData.scheduled_at}
                onChange={handleChange}
                required
                className="form-input-gaming"
              />
            </div>

            {/* Venue */}
            <div className="form-group-gaming">
              <VenueSearch
                label="📍 Venue"
                value={{
                  venue:          formData.venue,
                  venue_address:  formData.venue_address,
                  venue_lat:      formData.venue_lat,
                  venue_lng:      formData.venue_lng,
                  venue_place_id: formData.venue_place_id,
                  court:          formData.court,
                }}
                onChange={handleVenueChange}
                placeholder="Search park, school, gym..."
              />
            </div>

            {/* Notes */}
            <div className="form-group-gaming">
              <label className="form-label-gaming">Additional Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Parking, gear requirements, meetup spot..."
                rows={2}
                maxLength={1000}
                className="form-input-gaming"
                style={{ resize: 'vertical', fontFamily: 'inherit' }}
              />
            </div>
          </FloatingCard>

          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
            <button
              type="button"
              className="dropin-btn dropin-btn--secondary"
              onClick={() => navigate(`/drop-in/${id}`)}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="dropin-btn dropin-btn--primary"
              disabled={saving}
              style={{ flex: 1 }}
            >
              {saving ? 'Saving…' : '💾 Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </GameLayout>
  );
};

export default DropInEdit;
